<template>
  <div class="aic-container">
    <h2>АИК - Административный индивидуальный контроль</h2>
    <div class="stats-header">
      <div class="stat-card">
        <span class="stat-number">{{ studentsList.length }}</span>
        <span class="stat-label">учеников на АИК</span>
      </div>
      <button class="btn-print" @click="printAllStudentsData">
        🖨️ Печать полного отчета
      </button>
    </div>

    <!-- ТАБЛИЧНАЯ ФОРМА -->
    <div class="students-table-wrapper">
      <table class="students-table">
        <thead>
          <tr>
            <th>№</th>
            <th>Класс</th>
            <th>ФИО ученика</th>

            <th>Дата постановки на АИК</th>
            <th>Всего проверок</th>
            <th>Баллы (последние 3)</th>
            <th>Последняя проверка</th>
            <th>Эмоциональный риск</th>
            <th>Риск по тепловой карте</th>
            <th>Действия</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(student, index) in studentsList"
            :key="student._id"
            :class="{ 'old-check-row': isCheckOld(student) }"
          >
            <td class="student-number">{{ index + 1 }}</td>
            <td>{{ getDisplayCLassName(student) }}</td>
            <td class="student-name-cell">
              <strong>{{ student.FirstName }} {{ student.LastName }} </strong>
            </td>

            <td>{{ getAICDate(student) }}</td>
            <td class="text-center">{{ getTotalChecks(student) }}</td>
            <td :class="getScoreClass(getLastThreeScores(student))">
              {{ getLastThreeScores(student) }}
            </td>
            <td :class="{ 'old-check': isCheckOld(student) }">
              {{ getLastCheckDateString(student) }}
            </td>
            <td :class="getRiskClass(student)">
              {{ getEmotionalRisk(student) }}
            </td>
            <td :class="getRiskZoneClass(student)">
              {{ getRiskZoneName(student) }}
            </td>
            <td class="actions-cell">
              <button
                class="btn-primary btn-small"
                @click="openCreateAICmodel(student)"
              >
                Добавить проверку
              </button>
              <button
                class="btn-secondary btn-small"
                @click="openStudentDetails(student)"
              >
                Подробнее
              </button>
              <button
                v-if="getLastThreeScores(student) >= 39 && !isCheckOld(student)"
                @click="deleteStudentToAIC(student)"
                class="btn-success btn-small"
                :title="'Возможность появилась, т.к. ученик набрал нужное количество баллов за последние проверки (39)'"
              >
                Снять с АИК
              </button>
              <button
                @click="deleteStudentToAIC(student)"
                class="btn-danger btn-small"
                :title="'Ученик еще не набрал нужного количества баллов для снятия с АИК'"
              >
                Снять с АИК (без подтверждения)
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Модальное окно для деталей ученика -->
    <div
      v-if="showAllAicStudentModal"
      class="modal-overlay"
      @click="closeModal"
    >
      <div class="modal-content" @click.stop>
        <h3>Детальная информация по АИК</h3>
        <div class="student-details">
          <div class="details-header">
            <h4>
              {{ getDisplayCLassName(selectedStudent) }}
              {{ selectedStudent.LastName }} {{ selectedStudent.FirstName }}
            </h4>
            <button @click="closeModal" class="close-btn">×</button>
          </div>

          <div class="details-content">
            <div class="section">
              <h5>История проверок АИК</h5>
              <div
                v-if="
                  selectedStudent.AIC.checks &&
                  selectedStudent.AIC.checks.length > 0
                "
                class="checks-list"
              >
                <div
                  v-for="(check, index) in selectedStudent.AIC.checks"
                  :key="index"
                  class="check-item"
                >
                  <details>
                    <summary>
                      {{ formatDate(check.date) }} | Общий балл:
                      {{ calculateTotalScore(check) }} | Проверяющий:
                      {{ check.inspectorName }}
                    </summary>
                    <div>
                      <div class="check-header">
                        <span class="check-date">{{
                          formatDate(check.date)
                        }}</span>
                        <span class="check-total"
                          >Общий балл: {{ calculateTotalScore(check) }}</span
                        >
                      </div>

                      <div class="check-details">
                        <div class="criteria-grid">
                          <div
                            class="criteria-item"
                            v-if="check.evaluations.diary !== undefined"
                          >
                            <span class="criteria-label">Дневник: </span>
                            <span class="criteria-value">{{
                              getCriteriaText("diary", check.evaluations.diary)
                            }}</span>
                          </div>

                          <div
                            class="criteria-item"
                            v-if="check.evaluations.preparation !== undefined"
                          >
                            <span class="criteria-label">Подготовка: </span>
                            <span class="criteria-value">{{
                              getCriteriaText(
                                "preparation",
                                check.evaluations.preparation,
                              )
                            }}</span>
                          </div>

                          <div
                            class="criteria-item"
                            v-if="check.evaluations.homework !== undefined"
                          >
                            <span class="criteria-label">ДЗ: </span>
                            <span class="criteria-value">{{
                              getCriteriaText(
                                "homework",
                                check.evaluations.homework,
                              )
                            }}</span>
                          </div>

                          <div
                            class="criteria-item"
                            v-if="check.evaluations.appearance !== undefined"
                          >
                            <span class="criteria-label">Внешний вид: </span>
                            <span class="criteria-value">{{
                              getCriteriaText(
                                "appearance",
                                check.evaluations.appearance,
                              )
                            }}</span>
                          </div>

                          <div
                            class="criteria-item"
                            v-if="check.evaluations.behavior !== undefined"
                          >
                            <span class="criteria-label">Поведение: </span>
                            <span class="criteria-value">{{
                              getCriteriaText(
                                "behavior",
                                check.evaluations.behavior,
                              )
                            }}</span>
                          </div>

                          <div
                            class="criteria-item"
                            v-if="check.evaluations.performance !== undefined"
                          >
                            <span class="criteria-label">Успеваемость: </span>
                            <span class="criteria-value">{{
                              getCriteriaText(
                                "performance",
                                check.evaluations.performance,
                              )
                            }}</span>
                          </div>

                          <div
                            class="criteria-item"
                            v-if="check.evaluations.attendance !== undefined"
                          >
                            <span class="criteria-label">Посещаемость: </span>
                            <span class="criteria-value">{{
                              getCriteriaText(
                                "attendance",
                                check.evaluations.attendance,
                              )
                            }}</span>
                          </div>

                          <div
                            class="criteria-item"
                            v-if="
                              check.evaluations.extracurricular !== undefined
                            "
                          >
                            <span class="criteria-label">Занятость: </span>
                            <span class="criteria-value">{{
                              getCriteriaText(
                                "extracurricular",
                                check.evaluations.extracurricular,
                              )
                            }}</span>
                          </div>
                        </div>

                        <div class="check-comment" v-if="check.comment">
                          <strong>Комментарий: </strong> {{ check.comment }}
                        </div>
                      </div>
                    </div>
                  </details>
                </div>
              </div>
              <div v-else class="no-data">Проверок пока не было</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showAICModal && selectedStudent" class="modal-overlay">
      <AICModal
        v-if="showAICModal"
        :student-data="selectedStudent"
        @close="closeModal()"
        @saved="loadAICForAllStudents()"
      />
    </div>
  </div>
</template>
<script>
import TutorialDataService from "../../services/TutorialDataService";
import AICModal from "../AIC/AIC_create.vue";

export default {
  name: "AICStudentsList",
  components: {
    AICModal,
  },
  data() {
    return {
      studentsList: [],
      classList: [],
      showAICModal: false,
      selectedStudent: null,
      showAllAicStudentModal: false,
    };
  },
  methods: {
    // Добавьте эти вспомогательные методы
    getAICDate(student) {
      const date = student.AIC?.date || student.createdAt || new Date();
      return this.formatDate(date);
    },

    getLastCheckDateString(student) {
      const lastCheckDate = this.getLastCheckDate(student);
      return lastCheckDate ? this.formatDate(lastCheckDate) : "нет проверок";
    },

    getEmotionalRisk(student) {
      return student.emotionalState
        ? this.calculateRiskScore(student) + "%"
        : "нет данных";
    },

    formatDate(dateString) {
      if (!dateString) return "—";
      const date = new Date(dateString);
      return date.toLocaleDateString("ru-RU");
    },
    // Вспомогательные методы для классов
    getScoreClass(score) {
      if (score === "нет данных") return "score-neutral";
      if (score >= 39) return "score-normal";
      if (score >= 30) return "score-warning";
      return "score-danger";
    },

    getRiskClass(student) {
      const riskScore = student.emotionalState
        ? this.calculateRiskScore(student)
        : null;
      if (!riskScore) return "";
      if (riskScore >= 70) return "risk-high";
      if (riskScore >= 40) return "risk-medium";
      return "risk-low";
    },

    getRiskZoneClass(student) {
      const zone = this.getRiskZoneName(student);
      if (zone === "Высокий") return "risk-high";
      if (zone === "Средний") return "risk-medium";
      if (zone === "Низкий") return "risk-low";
      return "";
    },

    // Определение основной зоны риска для студента
    getStudentZone(student) {
      if (!student.riskZones) return "none";

      if (
        student.riskZones.red &&
        Object.values(student.riskZones.red).some((v) => v)
      ) {
        return "red";
      }
      if (
        student.riskZones.yellow &&
        Object.values(student.riskZones.yellow).some((v) => v)
      ) {
        return "yellow";
      }
      if (
        student.riskZones.green &&
        Object.values(student.riskZones.green).some((v) => v)
      ) {
        return "green";
      }
      return "none";
    },

    getRiskZoneName(student) {
      const zone = this.getStudentZone(student);
      const map = {
        red: "Высокий",
        yellow: "Средний",
        green: "Низкий",
        none: "Не определен",
      };
      return map[zone] || "Не определен";
    },

    calculateRiskScore(student) {
      if (!student.emotionalState) return 0;

      const state = student.emotionalState;
      let score = 0;
      let total = 0;

      const weights = {
        mood: 1,
        social: 1,
        behavior: 2,
        thoughts: 3,
        circumstances: 1.5,
      };

      Object.values(state.mood).forEach((val) => {
        if (val) score += weights.mood;
        total += weights.mood;
      });
      Object.values(state.social).forEach((val) => {
        if (val) score += weights.social;
        total += weights.social;
      });
      Object.values(state.behavior).forEach((val) => {
        if (val) score += weights.behavior;
        total += weights.behavior;
      });
      Object.values(state.thoughts).forEach((val) => {
        if (val) score += weights.thoughts;
        total += weights.thoughts;
      });
      Object.values(state.circumstances).forEach((val) => {
        if (val) score += weights.circumstances;
        total += weights.circumstances;
      });

      return total > 0 ? Math.round((score / total) * 100) : 0;
    },

    async deleteStudentToAIC(el) {
      if (confirm("Вы уверенны?")) {
        let data = { status: false };
        await TutorialDataService.deleteStudentToAIC(el._id, data)
          .then((response) => {
            console.log(response.data);
            this.getStudentsWithAIC();
            alert("Успешно");
          })
          .catch((e) => {
            console.log(e);
            alert("Ошибка");
          });
      }
    },

    calculateTotalScore(check) {
      if (!check.evaluations) return 0;
      return Object.values(check.evaluations).reduce(
        (sum, score) => sum + (score || 0),
        0,
      );
    },

    getCriteriaText(criteria, value) {
      const texts = {
        diary: ["Нет (0)", "Наличие (1)", "Все ДЗ заполнены (2)"],
        preparation: [
          "Нет (0)",
          "Все тетради (1)",
          "Все учебники и принадлежности (2)",
        ],
        homework: [
          "Не выполнено (0)",
          "Выполнено частично (1)",
          "Все выполнены в тетрадях (2)",
        ],
        appearance: [
          "Без формы / грубые нарушения (0)",
          "В форме, но с нарушениями (1)",
          "Отличный вид (2)",
        ],
        behavior: [
          "Не ведет урок, не учится (0)",
          "Участие удовлетворительное (1)",
          "Активен на уроке (2)",
        ],
        performance: [
          "Двойки, тройки (0)",
          "Тройки, четверки (1)",
          "Четверки, пятерки (2)",
        ],
        attendance: [
          "Пропуски по неуважительной причине (0)",
          "Мало пропусков по уважительной причине (1)",
          "Пропусков нет (2)",
        ],
        extracurricular: [
          "Не посещает кружки (0)",
          "Посещает один кружок (1)",
          "Посещает несколько кружков (2)",
        ],
      };

      return texts[criteria] ? texts[criteria][value] : value;
    },

    isCheckOld(student) {
      const lastCheckDate = this.getLastCheckDate(student);
      if (!lastCheckDate) return false;

      const threeDaysAgo = new Date();
      threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);

      return new Date(lastCheckDate) < threeDaysAgo;
    },

    openCreateAICmodel(stud) {
      this.selectedStudent = stud;
      this.showAICModal = true;
    },

    async getStudentsWithAIC() {
      try {
        const res = await TutorialDataService.getStudentsWithAIC();
        this.studentsList = this.sortStudentsByClass(res.data.data);
      } catch (error) {
        console.error("Error fetching students with AIC:", error);
      }
    },

    async getAllClass() {
      await TutorialDataService.getAllCLass()
        .then((response) => {
          this.classList = response.data.map(this.getDisplayTutorial);
        })
        .catch((e) => {
          console.log(e);
        });
    },

    getDisplayTutorial(data) {
      return {
        id: data._id,
        className: data.className,
      };
    },

    getDisplayCLassName(student) {
      for (let i = 0; i < this.classList.length; i++) {
        if (student.classID == this.classList[i].id) {
          return this.classList[i].className;
        }
      }
      return "error";
    },

    getTotalChecks(student) {
      return student.AIC.checks ? student.AIC.checks.length : 0;
    },

    async loadAICForAllStudents() {
      try {
        const response = await TutorialDataService.getAllAIC();
        const allAIC = response.data || response;

        const aicByStudent = {};

        allAIC.forEach((aic) => {
          if (!aicByStudent[aic.studentID]) {
            aicByStudent[aic.studentID] = [];
          }
          aicByStudent[aic.studentID].push(aic);
        });

        this.studentsList.forEach((student) => {
          let studentAIC = aicByStudent[student._id] || [];
          studentAIC = [...studentAIC].sort(
            (a, b) => new Date(b.date) - new Date(a.date),
          );

          if (student.AIC) {
            this.$set(student.AIC, "checks", studentAIC);
          } else {
            this.$set(student, "AIC", {
              checks: studentAIC,
            });
          }
        });

        console.log("AIC распределены по студентам (отсортированы по дате)");
      } catch (error) {
        console.error("Ошибка при загрузке AIC:", error);
        this.studentsList.forEach((student) => {
          this.$set(student, "AIC", { checks: [] });
        });
      }
    },
    sortStudentsByClass(students) {
      return students.sort((a, b) => {
        const classNameA = this.getDisplayCLassName(a);
        const classNameB = this.getDisplayCLassName(b);

        // Сортируем по классу (строковое сравнение)
        return classNameA.localeCompare(classNameB);
      });
    },

    getLastThreeScores(student) {
      if (!student.AIC?.checks || student.AIC.checks.length === 0) {
        return "нет данных";
      }

      const lastThree = [...student.AIC.checks]
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 3);

      const totalScore = lastThree.reduce((sum, check) => {
        if (check.evaluations && typeof check.evaluations === "object") {
          const scores = Object.values(check.evaluations).filter(
            (score) => typeof score === "number",
          );
          return sum + scores.reduce((subSum, score) => subSum + score, 0);
        }
        return sum;
      }, 0);

      return totalScore;
    },

    getLastCheckDate(student) {
      if (!student.AIC?.checks || student.AIC.checks.length === 0) {
        return null;
      }

      return [...student.AIC.checks].sort(
        (a, b) => new Date(b.date) - new Date(a.date),
      )[0].date;
    },

    openStudentDetails(student) {
      this.selectedStudent = student;
      this.showAllAicStudentModal = true;
    },

    closeModal() {
      this.selectedStudent = null;
      this.showAllAicStudentModal = false;
      this.showAICModal = false;
    },

    printAllStudentsData() {
      const printWindow = window.open("", "_blank");

      // Собираем строки таблицы проверок в массив
      let checksTableRows = [];

      this.studentsList.forEach((student, studentIndex) => {
        const checks = student.AIC?.checks || [];

        if (checks.length === 0) {
          checksTableRows.push(`
        <tr>
          <td colspan="15" style="text-align: center; color: #999;">
            Нет проверок у ученика ${student.LastName} ${
            student.FirstName
          } (${this.getDisplayCLassName(student)})
          </td>
        </tr>
      `);
        } else {
          checks.forEach((check, checkIndex) => {
            const totalScore = this.calculateTotalScore(check);
            checksTableRows.push(`
          <tr>
            <td>${studentIndex + 1}.${checkIndex + 1}</td>
            <td>${this.getDisplayCLassName(student)}</td>
            <td><strong>${student.LastName} ${student.FirstName}</strong></td>
            <td>${this.formatDate(check.date)}</td>
            <td><strong>${totalScore}</strong></td>
            <td>${check.inspectorName || "—"}</td>
            <td>${
              this.getCriteriaText("diary", check.evaluations?.diary) || "—"
            }</td>
            <td>${
              this.getCriteriaText(
                "preparation",
                check.evaluations?.preparation,
              ) || "—"
            }</td>
            <td>${
              this.getCriteriaText("homework", check.evaluations?.homework) ||
              "—"
            }</td>
            <td>${
              this.getCriteriaText(
                "appearance",
                check.evaluations?.appearance,
              ) || "—"
            }</td>
            <td>${
              this.getCriteriaText("behavior", check.evaluations?.behavior) ||
              "—"
            }</td>
            <td>${
              this.getCriteriaText(
                "performance",
                check.evaluations?.performance,
              ) || "—"
            }</td>
            <td>${
              this.getCriteriaText(
                "attendance",
                check.evaluations?.attendance,
              ) || "—"
            }</td>
            <td>${
              this.getCriteriaText(
                "extracurricular",
                check.evaluations?.extracurricular,
              ) || "—"
            }</td>
            <td style="max-width: 200px;">${check.comment || "—"}</td>
          </tr>
        `);
          });
        }
      });

      // Собираем строки таблицы учеников
      let studentsTableRows = [];

      this.studentsList.forEach((student, index) => {
        const totalChecks = this.getTotalChecks(student);
        const lastThreeScores = this.getLastThreeScores(student);
        const lastCheckDate = this.getLastCheckDate(student);
        const riskScore = student.emotionalState
          ? this.calculateRiskScore(student)
          : null;
        const riskZone = this.getRiskZoneName(student);
        const isCheckOld = this.isCheckOld(student);

        let scoreClass = "";
        if (lastThreeScores !== "нет данных") {
          if (lastThreeScores >= 39) scoreClass = "score-normal";
          else if (lastThreeScores >= 30) scoreClass = "score-warning";
          else scoreClass = "score-danger";
        }

        let riskClass = "";
        if (riskZone === "Высокий") riskClass = "risk-high";
        else if (riskZone === "Средний") riskClass = "risk-medium";
        else if (riskZone === "Низкий") riskClass = "risk-low";

        studentsTableRows.push(`
      <tr>
        <td>${index + 1}</td>
        <td><strong>${student.FirstName} ${student.LastName}</strong></td>
        <td>${this.getDisplayCLassName(student)}</td>
        <td>${this.formatDate(
          student.AIC?.date || student.createdAt || new Date(),
        )}</td>
        <td>${totalChecks}</td>
        <td class="${scoreClass}">${lastThreeScores}</td>
        <td class="${isCheckOld ? "old-check" : ""}">${
          lastCheckDate ? this.formatDate(lastCheckDate) : "нет проверок"
        }</td>
        <td>${riskScore ? riskScore + "%" : "нет данных"}</td>
        <td class="${riskClass}">${riskZone}</td>
      </tr>
    `);
      });

      printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Отчет по АИК - ${new Date().toLocaleDateString("ru-RU")}</title>
      <meta charset="UTF-8">
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          padding: 20px;
          background: white;
          font-size: 12px;
        }
        
        .report-header {
          text-align: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 2px solid #333;
        }
        
        .report-header h1 {
          font-size: 18px;
          margin-bottom: 8px;
        }
        
        .report-header p {
          color: #666;
          font-size: 12px;
        }
        
        /* Основная таблица учеников */
        .students-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 30px;
          page-break-after: avoid;
        }
        
        .students-table th {
          background: #f0f0f0;
          border: 1px solid #ddd;
          padding: 10px 8px;
          text-align: left;
          font-weight: bold;
          font-size: 12px;
        }
        
        .students-table td {
          border: 1px solid #ddd;
          padding: 8px;
          vertical-align: top;
          font-size: 11px;
        }
        
        /* Таблица проверок */
        .checks-title {
          font-size: 14px;
          font-weight: bold;
          margin: 20px 0 10px 0;
          padding-top: 10px;
          border-top: 2px solid #333;
        }
        
        .checks-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
          font-size: 10px;
        }
        
        .checks-table th {
          background: #f0f0f0;
          border: 1px solid #ddd;
          padding: 8px 6px;
          text-align: left;
          font-weight: bold;
          font-size: 10px;
        }
        
        .checks-table td {
          border: 1px solid #ddd;
          padding: 6px;
          font-size: 9px;
          vertical-align: top;
        }
        
        /* Цвета */
        .risk-high {
          color: #c62828;
          font-weight: bold;
        }
        
        .risk-medium {
          color: #f57c00;
          font-weight: bold;
        }
        
        .risk-low {
          color: #2e7d32;
          font-weight: bold;
        }
        
        .score-normal {
          color: #2e7d32;
          font-weight: bold;
        }
        
        .score-warning {
          color: #f57c00;
          font-weight: bold;
        }
        
        .score-danger {
          color: #c62828;
          font-weight: bold;
        }
        
        .old-check {
          color: #c62828;
        }
        
        .footer {
          margin-top: 30px;
          text-align: center;
          font-size: 10px;
          color: #666;
          border-top: 1px solid #ddd;
          padding-top: 10px;
        }
        
        @media print {
          body {
            padding: 10px;
          }
          
          .students-table {
            page-break-after: avoid;
          }
          
          .checks-table {
            page-break-before: avoid;
          }
        }
      </style>
    </head>
    <body>
      <div class="report-header">
        <h1>Отчет по административному индивидуальному контролю (АИК)</h1>
        <p>Дата формирования: ${new Date().toLocaleString("ru-RU")}</p>
        <p>Всего учеников на АИК: ${this.studentsList.length}</p>
      </div>
      
      <!-- ОСНОВНАЯ ТАБЛИЦА УЧЕНИКОВ -->
      <table class="students-table">
        <thead>
          <tr>
            <th>№</th>
            <th>ФИО ученика</th>
            <th>Класс</th>
            <th>Дата постановки на АИК</th>
            <th>Всего проверок</th>
            <th>Баллы (последние 3)</th>
            <th>Последняя проверка</th>
            <th>Эмоциональный риск</th>
            <th>Риск по тепловой карте</th>
          </tr>
        </thead>
        <tbody>
          ${studentsTableRows.join("")}
        </tbody>
      </table>
      
      <!-- ТАБЛИЦА ПРОВЕРОК -->
      <div class="checks-title">📋 Детализация всех проверок</div>
      <table class="checks-table">
        <thead>
          <tr>
            <th>№</th>
            <th>Класс</th>
            <th>ФИО ученика</th>
            <th>Дата проверки</th>
            <th>Балл</th>
            <th>Проверяющий</th>
            <th>Дневник</th>
            <th>Подготовка</th>
            <th>ДЗ</th>
            <th>Внешний вид</th>
            <th>Поведение</th>
            <th>Успеваемость</th>
            <th>Посещаемость</th>
            <th>Занятость</th>
            <th>Комментарий</th>
          </tr>
        </thead>
        <tbody>
          ${checksTableRows.join("")}
        </tbody>
      </table>
      
      <div class="footer">
        <p>Отчет сформирован автоматически | ${new Date().toLocaleString()}</p>
      </div>
    </body>
    </html>
  `);

      printWindow.document.close();

      printWindow.onload = () => {
        printWindow.print();
      };
    },
  },

  async mounted() {
    await this.getAllClass();
    await this.getStudentsWithAIC();
    await this.loadAICForAllStudents();
  },
};
</script>

<style scoped>
.aic-container {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.stats-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 15px;
}

.stat-card {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  border-left: 4px solid #007bff;
  flex: 1;
}

.stat-number {
  display: block;
  font-size: 2rem;
  font-weight: bold;
  color: #007bff;
}

.stat-label {
  color: #6c757d;
  font-size: 0.9rem;
}

.btn-print {
  background: #28a745;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.3s ease;
}

.btn-print:hover {
  background: #218838;
}

/* Таблица */
.students-table-wrapper {
  overflow-x: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.students-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  font-size: 13px;
}

.students-table th {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  padding: 12px 10px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  white-space: nowrap;
}

.students-table td {
  border: 1px solid #dee2e6;
  padding: 10px;
  vertical-align: middle;
}

.student-number {
  color: #6c757d;
  font-weight: 500;
  text-align: center;
}

.student-name-cell {
  font-weight: 500;
}

.text-center {
  text-align: center;
}

.actions-cell {
  white-space: nowrap;
  min-width: 280px;
}

/* Кнопки */
.btn-small {
  padding: 5px 10px;
  font-size: 11px;
  margin: 0 2px;
}

.btn-primary {
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-primary:hover {
  background: #0056b3;
}

.btn-secondary {
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-secondary:hover {
  background: #545b62;
}

.btn-success {
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-success:hover {
  background: #218838;
}

/* Цветовые классы */
.score-normal {
  color: #2e7d32;
  font-weight: bold;
}

.score-warning {
  color: #f57c00;
  font-weight: bold;
}

.score-danger {
  color: #c62828;
  font-weight: bold;
}

.score-neutral {
  color: #6c757d;
}

.risk-high {
  color: #c62828;
  font-weight: bold;
}

.risk-medium {
  color: #f57c00;
  font-weight: bold;
}

.risk-low {
  color: #2e7d32;
  font-weight: bold;
}

.old-check {
  color: #c62828;
  font-weight: 500;
}

.old-check-row {
  background-color: #fff3f3;
}

/* Модальное окно */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.student-details {
  min-height: 200px;
}

.details-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
}

.section {
  margin-bottom: 20px;
}

.checks-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.check-item {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 10px;
  background: #f8f9fa;
}

.check-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.criteria-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px;
  margin-bottom: 10px;
}

.criteria-item {
  display: flex;
  justify-content: space-between;
  padding: 4px 8px;
  background: white;
  border-radius: 4px;
}

.check-comment {
  margin-top: 10px;
  padding: 8px;
  background: white;
  border-radius: 4px;
  color: #6c757d;
  font-style: italic;
}

.no-data {
  text-align: center;
  color: #6c757d;
  padding: 20px;
}

@media (max-width: 768px) {
  .actions-cell {
    min-width: 200px;
  }

  .btn-small {
    padding: 4px 6px;
    font-size: 10px;
  }
}
</style>
